/* Utilities to manage per-inventory errors in localStorage */

const ERRORS_PREFIX = 'inventory:errors:';
const MAX_ERRORS = 50;
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function pruneOldErrors(errors, ttlMs = DEFAULT_TTL_MS) {
  const now = Date.now();
  return (errors || []).filter((e) => {
    const t = Date.parse(e.time || '');
    return Number.isFinite(t) ? (now - t) < ttlMs : true;
  });
}

export function getErrorsKey(inventoryId) {
  return `${ERRORS_PREFIX}${inventoryId}`;
}

export function readErrors(inventoryId, { ttlMs = DEFAULT_TTL_MS } = {}) {
  if (!inventoryId) return [];
  try {
    const raw = localStorage.getItem(getErrorsKey(inventoryId));
    const data = raw ? JSON.parse(raw) : [];
    return pruneOldErrors(Array.isArray(data) ? data : [], ttlMs);
  } catch (_) {
    return [];
  }
}

// Only explicit publication errors need tracking after their journal was removed
// by a successful concurrent attempt/tab. Never scan server inventory history.
export function readPublicationErrors() {
  try {
    return Object.keys(localStorage).filter((key) => key.startsWith(ERRORS_PREFIX)).flatMap((key) => {
      const inventoryId = key.slice(ERRORS_PREFIX.length);
      return readErrors(inventoryId).filter((error) => error.type === 'update' && error.updateId)
        .map((error) => ({ ...error, inventoryId }));
    });
  } catch (_) { return []; }
}

export function saveErrors(inventoryId, errors) {
  if (!inventoryId) return;
  try {
    localStorage.setItem(getErrorsKey(inventoryId), JSON.stringify(errors));
  } catch (_) { /* no-op */ }
}

export function addError(inventoryId, errorEntry, { ttlMs = DEFAULT_TTL_MS } = {}) {
  if (!inventoryId) return [];
  const prev = readErrors(inventoryId, { ttlMs });
  // Retrying/reloading a legacy batch cannot reconstruct its missing payload.
  // Keep one diagnostic per record until publication clears the batch's errors.
  if (errorEntry.type === 'missing-record' && prev.some((entry) => (
    entry.type === errorEntry.type && entry.updateId === errorEntry.updateId && entry.recordId === errorEntry.recordId
  ))) return prev;
  const next = [...prev, {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    time: new Date().toISOString(),
    ...errorEntry,
  }].slice(-MAX_ERRORS);
  saveErrors(inventoryId, next);
  try {
    window.dispatchEvent(new CustomEvent('inventoryErrorsChanged', {
      detail: { inventoryId, errors: next },
    }));
  } catch (_) { /* no-op */ }
  return next;
}

export function clearErrors(inventoryId) {
  if (!inventoryId) return;
  try {
    localStorage.removeItem(getErrorsKey(inventoryId));
    window.dispatchEvent(new CustomEvent('inventoryErrorsChanged', {
      detail: { inventoryId, errors: [] },
    }));
  } catch (_) { /* no-op */ }
}

export function clearErrorsForContainer(inventoryId, containerId) {
  if (!inventoryId || !containerId) return [];
  const all = readErrors(inventoryId);
  const next = all.filter((e) => e.containerId !== containerId);
  saveErrors(inventoryId, next);
  try {
    window.dispatchEvent(new CustomEvent('inventoryErrorsChanged', {
      detail: { inventoryId, errors: next },
    }));
  } catch (_) { /* no-op */ }
  return next;
}

export function clearErrorsForUpdateId(inventoryId, updateId) {
  if (!inventoryId || !updateId) return [];
  const all = readErrors(inventoryId);
  const next = all.filter((e) => e.updateId !== updateId);
  saveErrors(inventoryId, next);
  try {
    window.dispatchEvent(new CustomEvent('inventoryErrorsChanged', {
      detail: { inventoryId, errors: next },
    }));
  } catch (_) { /* no-op */ }
  return next;
}
